import { Component, ElementRef, Inject, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { faX, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { NgbActiveModal, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { VerifyAccountModel } from 'src/app/models/verify-account.model';
import { AuthService } from 'src/app/services/auth/auth.service';
import { InputTextComponent } from '../../inputs/input-text/input-text.component';
import { interval, Subject, Subscription } from 'rxjs';
import { switchMap, takeWhile, map, startWith, takeUntil, finalize, concatMap } from 'rxjs/operators';
import { InfoMessageService } from 'src/app/services/info-message.service';
import { DOCUMENT } from '@angular/common';
import { RecoverPasswordComponent } from '../recover-password/recover-password.component';
import { RegisterComponent } from '../register/register.component';

@Component({
  selector: 'app-verify-account',
  templateUrl: './verify-account.component.html',
  styleUrls: ['./verify-account.component.scss'],
})
export class VerifyAccountComponent {
  @ViewChild('firstInput') firstInput: InputTextComponent;
  @ViewChild('secondInput') secondInput: InputTextComponent;
  @ViewChild('thirdInput') thirdInput: InputTextComponent;
  @ViewChild('fourthInput') fourthInput: InputTextComponent;
  @ViewChild('btnResend') btnResend: ElementRef<HTMLAnchorElement>;
  @Input() title = '';
  @Input() msg = '';
  @Input() method = '';
  @Input() btn_msg_yes = '';
  @Input() btn_msg_no = '';
  @Input() callback_yes = () => {};
  @Input() callback_no = () => {};
  confirmAccount: RxFormGroup;
  faClose = faX;
  faChevronLeft = faChevronLeft;
  loginCarg = false;
  correoEnviado = false;
  modalWelcome?: NgbModalRef;
  ngbModalOptions: NgbModalOptions = {
      backdropClass: 'modal-backdrop-blur',
      backdrop: 'static',
      keyboard: false,
      centered: true,
  };
  isLogin = false;
  remainingTime: number = 30;
  private destroy$ = new Subject<void>();
  private reset$ = new Subject<void>();
  private counterSubscription: Subscription;
  private isCounterActive = false;
  private isCounterCompleted = false;

  private verifyAccountSubscription: Subscription;
  private loginSubscription: Subscription;
  private resendCodeSubscription: Subscription;

  constructor(
      public activeModal: NgbActiveModal,
      private fb: RxFormBuilder,
      private router: Router,
      private authService: AuthService,
      private modalService: NgbModal,
      private messageService: InfoMessageService,
      @Inject(DOCUMENT) private document: Document
    ) {}

  async ngOnInit(): Promise<void> {
      await this.initForm();
  }

  startCountdown() {
    
    this.counterSubscription = this.reset$.pipe(
      startWith(null),
      switchMap(() => interval(1000).pipe(
        startWith(-1),
        map(secondsElapsed => 30 - (secondsElapsed + 1)),
        takeWhile(remaining => remaining > 0),
        finalize(() => {
          this.isCounterCompleted = true;
        }),
        takeUntil(this.destroy$),
        takeUntil(this.reset$)
      ))
    ).subscribe({
      next: (val) => {
        this.remainingTime = val;
      }
    });
  }

  resetCountdown() {
    this.loginCarg = false;
    this.isCounterCompleted = false;
    if(!this.isCounterActive){
      this.isCounterActive = true;
      this.startCountdown();
    }
    else{
      this.reset$.next();
    }
    this.confirmAccount.resetForm();
    this.confirmAccount.markAsUntouched();
    this.confirmAccount.markAsPristine();
    this.messageService.showMessage(
      "¡Código reenviado!",
      "Hemos enviado un nuevo código de validación, te llegará en unos minutos.",
      "success"
    );
  }
  
  async initForm(): Promise<void> {
    return new Promise((resolve) => {
      const verifyModel = new VerifyAccountModel();
      this.confirmAccount = this.fb.formGroup(verifyModel) as RxFormGroup;
      this.isCounterCompleted = false;
      this.isCounterActive = true;
      this.startCountdown();
      resolve();
    });
  }

  onNo() {
    this.activeModal.close('Close click');
    this.messageService.hideActiveMessage();
    if(this.method == 'register'){
      this.modalWelcome = this.modalService.open(
        RegisterComponent,
        this.ngbModalOptions
      );
    }
    else if(this.method == 'recover-password'){
      this.modalWelcome = this.modalService.open(
        RecoverPasswordComponent,
        this.ngbModalOptions
      );
    }
  }

  moveFocus(event: any, nextInput: InputTextComponent, prevInput: InputTextComponent, controlName: string) {
      const value = event.target.value.substring(0, 1);
      this.confirmAccount.get(controlName).setValue(value);
  
      if (value.length === 1) {
        if (nextInput) {
          nextInput.focusOnInputText();
        }
      }
  }

  handleKeyDown(event: KeyboardEvent, nextInput: InputTextComponent, prevInput: InputTextComponent, controlName: string) {
      const target = event.target as HTMLInputElement;

      if (event.key === 'Backspace' && target.value.length === 0 && prevInput) {
        prevInput.focusOnInputText();
        setTimeout(() => {
          const prevControlName = this.getPrevControlName(controlName);
          if (prevControlName) {
            this.confirmAccount.get(prevControlName).setValue('');
          }
        }, 0);
      }
  }

  private getPrevControlName(currentControlName: string): string | null {
      const controlsOrder = ['firstCode', 'secondCode', 'thirdCode', 'fourthCode'];
      const index = controlsOrder.indexOf(currentControlName);
      return index > 0 ? controlsOrder[index - 1] : null;
  }

  handlePaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData.getData('text').trim();

    if (pastedText.length === 4 && /^\d+$/.test(pastedText)) {
      const digits = pastedText.split('');

      this.confirmAccount.get('firstCode').setValue(digits[0]);
      this.confirmAccount.get('secondCode').setValue(digits[1]);
      this.confirmAccount.get('thirdCode').setValue(digits[2]);
      this.confirmAccount.get('fourthCode').setValue(digits[3]);

      this.fourthInput.focusOnInputText();
    }
  }

  verifyAccount(){
    if (this.confirmAccount.valid) {
    const { firstCode, secondCode, thirdCode, fourthCode } = this.confirmAccount.value;
    const concantOtp = String(firstCode + secondCode + thirdCode + fourthCode);
    const value = this.authService.tempUserRegistrationData?.phone ? 
    this.authService.tempUserRegistrationData.phone : this.authService.tempUserRegistrationData?.email; 
    this.loginCarg = true;
    if(this.method == 'register'){
      this.verifyAccountSubscription = this.authService.verifyAccount(value, concantOtp, null, this.document.location.origin).subscribe({
        next: async (response) => {
          this.loginCarg = false;
           this.loginUser(value, this.authService.tempUserRegistrationData?.password);
        },
        error: (error) => {
          this.loginCarg = false;
          this.confirmAccount.resetForm();
          this.confirmAccount.markAsUntouched();
          this.confirmAccount.markAsPristine();
        },
        complete: () => {
          this.authService.tempUserRegistrationData = {};
          this.loginCarg = false;
        }
      });
    }
    else{
      this.verifyAccountSubscription = this.authService.verifyAccount(value, concantOtp, 'recover-password').subscribe({
        next: async (response) => {
          this.authService.tempUserRegistrationData = {
            ...this.authService.tempUserRegistrationData,
            otp: concantOtp
          };
          localStorage.setItem('recoverToken', response.token);
          this.activeModal.close('register');
          this.modalWelcome = this.modalService.open(RecoverPasswordComponent, this.ngbModalOptions);
          this.modalWelcome.componentInstance.isVerified = true;
        },
        error: (error) => {
          this.loginCarg = false;
          this.confirmAccount.resetForm();
          this.confirmAccount.markAsUntouched();
          this.confirmAccount.markAsPristine();
        }
      });
    }
    
    } else {
      // Mark all controls as touched to display validation errors
      this.confirmAccount.markAllAsTouched();
    }
  }

  getUserByIdAndToken(id: string, token: string){
    this.authService.getUserByIdAndToken(id, token).subscribe({
      next: (response) => {
        this.loginUser(response.email, response.password);
      }
    });
  }

  loginUser(email: any, password: any) {
    this.loginSubscription = this.authService.login(email, password).subscribe({
      next: async (responseLogin) => {
        await this.authService.setData(responseLogin);
        this.onYes();
      },
      error: (error) => {
        this.isLogin = false; // Hide loader
      },
    });
  }
  welcome() {
    this.activeModal.close('register');
    /* this.modalWelcome = this.modalService.open(
      PostRegistrationWelcomeComponent,
      this.ngbModal
    ); */
    this.modalWelcome.result.then(
      () => {},
      () => {
        this.activeModal.close('login');
        this.callback_yes();
      }
    );
  }

  onYes() {
    this.activeModal.close('Close click');
    this.callback_yes();
    // Forzar actualización del header después de cerrar el modal
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  ngOnDestroy(){
    this.destroy$.next();
    this.destroy$.complete();
    this.counterSubscription?.unsubscribe();
    if(this.verifyAccountSubscription){
      this.verifyAccountSubscription.unsubscribe();
    }
    if(this.loginSubscription){
      this.loginSubscription.unsubscribe();
    }
    if(this.resendCodeSubscription){
      this.resendCodeSubscription.unsubscribe();
    }
  }

  resendOTP() {
    if (this.authService.tempUserRegistrationData){
      this.loginCarg = true;
      if(this.method == 'register'){
        const { email, phone, password, name, lastname } = this.authService.tempUserRegistrationData;
        const emailOrPhone = phone ? phone : email;
        this.resendCodeSubscription = this.authService.register(emailOrPhone, password, name, lastname, this.document.location.origin).subscribe({
          next: async (response) => {
            this.resetCountdown();
          },
          error: (error) => {
            this.loginCarg = false;
          }
        });
      }
      else if(this.method == 'recover-password'){
        const { email, phone } = this.authService.tempUserRegistrationData;
        const emailOrPhone = phone ? phone : email;
        this.resendCodeSubscription = this.authService.recover(emailOrPhone, this.document.location.origin).subscribe({
          next: async (response) => {
            this.resetCountdown();
          },
          error: (error) => {
            this.loginCarg = false;
          }
        });
      }
    }
  }
}
